import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { SkeletonTable, LoadingRegion } from '../components/Skeleton'
import useTimerEntrySaved from '../hooks/useTimerEntrySaved'
import HelpButton from '../components/HelpButton'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'


const fmt = (val) => {
  const num = parseFloat(val) || 0
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function Billing() {
  const { user } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('active')
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('client')
  const [sortDir, setSortDir] = useState('asc')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const navigate = useNavigate()
  const canInvoice = user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_create_invoices

  useEffect(() => {
    fetchBilling()
  }, [])
  useTimerEntrySaved(() => fetchBilling())

  usePageShortcut('f', () => document.querySelector('input[placeholder*="Search"], input[placeholder*="Filter"], input[type="search"]')?.focus(), { enabled: true })
  usePageShortcut('b', () => navigate('/batch-invoicing', { state: { selectedMatterIds: [...selectedIds] } }), { enabled: canInvoice && selectedIds.size > 0 })

  const fetchBilling = async () => {
    try {
      const res = await api.get('/api/billing/summary')
      setData(res.data)
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view billing data.')
      } else {
        setError(err.response?.data?.error || err.message || 'Unable to load billing data.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('asc')
    }
  }

  const sortIcon = (col) => {
    if (sortBy !== col) return '↕'
    return sortDir === 'asc' ? '↑' : '↓'
  }

  if (loading) return (
    <LoadingRegion label="Loading billing">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="h-8 w-40 bg-gray-100 rounded animate-pulse mb-4" />
        <SkeletonTable rows={8} cols={6} />
      </div>
    </LoadingRegion>
  )

  if (!data) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-red-500">{error || 'Unable to load billing data.'}</p>
    </div>
  )

  let matters = data.matters || []

  // Filter by status
  if (filter === 'active') {
    matters = matters.filter(m => m.status === 'active')
  } else if (filter === 'closed') {
    matters = matters.filter(m => m.status !== 'active')
  } else if (filter === 'balance') {
    matters = matters.filter(m =>
      parseFloat(m.unposted_fees) > 0 || parseFloat(m.wip_fees) > 0 ||
      parseFloat(m.wip_expenses) > 0 || parseFloat(m.ar_balance) > 0
    )
  }

  // Search
  if (search) {
    const q = search.toLowerCase()
    matters = matters.filter(m =>
      m.title?.toLowerCase().includes(q) ||
      `${m.first_name} ${m.last_name}`.toLowerCase().includes(q)
    )
  }

  // Sort
  matters = [...matters].sort((a, b) => {
    let va, vb
    if (sortBy === 'client') {
      va = `${a.last_name} ${a.first_name}`.toLowerCase()
      vb = `${b.last_name} ${b.first_name}`.toLowerCase()
    } else if (sortBy === 'matter') {
      va = (a.title || '').toLowerCase()
      vb = (b.title || '').toLowerCase()
    } else {
      va = parseFloat(a[sortBy]) || 0
      vb = parseFloat(b[sortBy]) || 0
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1
    if (va > vb) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  // can_see_trust / can_see_ar come from the API — when false, the corresponding
  // column + totals are hidden entirely. row values are null in that case so we
  // must skip them in the sum.
  const canSeeTrust = data.can_see_trust !== false
  const canSeeAR = data.can_see_ar !== false
  const totals = matters.reduce((acc, row) => ({
    unposted_fees: acc.unposted_fees + parseFloat(row.unposted_fees),
    wip_fees: acc.wip_fees + parseFloat(row.wip_fees),
    wip_expenses: acc.wip_expenses + parseFloat(row.wip_expenses),
    ar_balance: canSeeAR ? acc.ar_balance + parseFloat(row.ar_balance || 0) : null,
    gen_retainer: acc.gen_retainer + parseFloat(row.gen_retainer),
    trust_balance: canSeeTrust ? acc.trust_balance + parseFloat(row.trust_balance || 0) : null,
  }), { unposted_fees: 0, wip_fees: 0, wip_expenses: 0, ar_balance: canSeeAR ? 0 : null, gen_retainer: 0, trust_balance: canSeeTrust ? 0 : null })

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Billing<HelpButton slug="billing" /></h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing {matters.length} Active Files
          </p>
        </div>
        {canInvoice && selectedIds.size > 0 && (
          <button
            onClick={() => navigate('/batch-invoicing', { state: { selectedMatterIds: [...selectedIds] } })}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Batch Invoice ({selectedIds.size} selected)
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Show:</label>
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {[
                { id: 'active', label: 'Active' },
                { id: 'all', label: 'All' },
                { id: 'closed', label: 'Closed' },
                { id: 'balance', label: 'With Balance' }
              ].map(f => (
                <button key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    filter === f.id
                      ? 'bg-white text-gray-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by client or matter..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Billing Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-800 text-white">
                {canInvoice && (
                  <th className="px-2 py-3 w-10">
                    <input type="checkbox"
                      checked={matters.length > 0 && matters.every(m => selectedIds.has(m.id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedIds(new Set(matters.map(m => m.id)))
                        else setSelectedIds(new Set())
                      }}
                      className="rounded border-gray-400 text-primary-600 focus:ring-primary-500"
                      onClick={e => e.stopPropagation()} />
                  </th>
                )}
                <th className="text-left px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                  onClick={() => handleSort('client')}>
                  Client {sortIcon('client')}
                </th>
                <th className="text-left px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                  onClick={() => handleSort('matter')}>
                  File / Matter {sortIcon('matter')}
                </th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                  onClick={() => handleSort('unposted_fees')}>
                  Unposted Fees {sortIcon('unposted_fees')}
                </th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                  onClick={() => handleSort('wip_fees')}>
                  WIP Fees {sortIcon('wip_fees')}
                </th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                  onClick={() => handleSort('wip_expenses')}>
                  WIP Expenses {sortIcon('wip_expenses')}
                </th>
                {/* Batch D-D.2: Oldest WIP age column — surfaces stale
                    unbilled work directly on the summary table.  Same
                    eligibility as the Stale WIP Dashboard KPI. */}
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                  onClick={() => handleSort('oldest_wip_days')}
                  title="Age of the oldest unbilled billable time entry on this matter (days).  Stale WIP is at high risk of write-off.">
                  Oldest WIP {sortIcon('oldest_wip_days')}
                </th>
                {canSeeAR && (
                  <th className="text-right px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                    onClick={() => handleSort('ar_balance')}>
                    AR {sortIcon('ar_balance')}
                  </th>
                )}
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                  onClick={() => handleSort('gen_retainer')}>
                  Gen Retainer {sortIcon('gen_retainer')}
                </th>
                {canSeeTrust && (
                  <th className="text-right px-4 py-3 font-medium cursor-pointer hover:bg-gray-700 select-none"
                    onClick={() => handleSort('trust_balance')}>
                    Trust {sortIcon('trust_balance')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {matters.length === 0 ? (
                <tr>
                  <td colSpan={(canInvoice ? 10 : 9) - (canSeeTrust ? 0 : 1) - (canSeeAR ? 0 : 1)} className="px-4 py-8 text-center text-gray-400">
                    No matters match your filters.
                  </td>
                </tr>
              ) : (
                matters.map(m => {
                  const hasActivity = parseFloat(m.unposted_fees) > 0 || parseFloat(m.wip_fees) > 0 ||
                    parseFloat(m.wip_expenses) > 0 || parseFloat(m.ar_balance) > 0
                  return (
                    <tr key={m.id} tabIndex={0} role="button"
                      onKeyDown={e => { if (e.key === 'Enter') navigate(`/matters/${m.id}?tab=billing`) }}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      hasActivity ? '' : 'text-gray-400'
                    }`} onClick={() => navigate(`/matters/${m.id}?tab=billing`)}>
                      {canInvoice && (
                        <td className="px-2 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(m.id)}
                            onChange={() => {
                              const next = new Set(selectedIds)
                              if (next.has(m.id)) next.delete(m.id)
                              else next.add(m.id)
                              setSelectedIds(next)
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                        </td>
                      )}
                      <td className="px-4 py-2.5 font-medium text-gray-700 whitespace-nowrap">
                        {m.first_name} {m.last_name}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 max-w-xs truncate">
                        {m.title}
                      </td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${
                        parseFloat(m.unposted_fees) > 0 ? 'text-gray-800' : 'text-gray-300'
                      }`}>
                        {fmt(m.unposted_fees)}
                      </td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${
                        parseFloat(m.wip_fees) > 0 ? 'text-gray-800' : 'text-gray-300'
                      }`}>
                        {fmt(m.wip_fees)}
                      </td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${
                        parseFloat(m.wip_expenses) > 0 ? 'text-gray-800' : 'text-gray-300'
                      }`}>
                        {fmt(m.wip_expenses)}
                      </td>
                      {/* Batch D-D.2: Oldest WIP days.  Amber when > 60 days
                          (aligns with Dashboard Stale WIP threshold). */}
                      <td className={`px-4 py-2.5 text-right tabular-nums ${
                        parseInt(m.oldest_wip_days, 10) > 60
                          ? 'text-amber-700 font-semibold'
                          : parseInt(m.oldest_wip_days, 10) > 0
                            ? 'text-gray-800'
                            : 'text-gray-300'
                      }`}>
                        {parseInt(m.oldest_wip_days, 10) > 0 ? `${m.oldest_wip_days}d` : '—'}
                      </td>
                      {canSeeAR && (
                        <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${
                          parseFloat(m.ar_balance) > 0 ? 'text-red-600' : 'text-gray-300'
                        }`}>
                          {fmt(m.ar_balance)}
                        </td>
                      )}
                      <td className={`px-4 py-2.5 text-right tabular-nums ${
                        parseFloat(m.gen_retainer) > 0 ? 'text-gray-800' : 'text-gray-300'
                      }`}>
                        {fmt(m.gen_retainer)}
                      </td>
                      {canSeeTrust && (
                        <td className={`px-4 py-2.5 text-right tabular-nums ${
                          parseFloat(m.trust_balance) > 0 ? 'text-green-600' : 'text-gray-300'
                        }`}>
                          {fmt(m.trust_balance)}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
            {matters.length > 0 && (
              <tfoot>
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td className="px-4 py-3 text-gray-800" colSpan={canInvoice ? 3 : 2}>Totals</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-800">{fmt(totals.unposted_fees)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-800">{fmt(totals.wip_fees)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-800">{fmt(totals.wip_expenses)}</td>
                  {/* Empty spacer for the "Oldest WIP" column — the column
                      shows a per-matter age chip not a summable value, so
                      the totals row leaves the slot blank.  Without this
                      cell every dollar total below is shifted one column
                      to the left of its header. */}
                  <td className="px-4 py-3 text-right text-gray-300">—</td>
                  {canSeeAR && (
                    <td className="px-4 py-3 text-right tabular-nums text-red-600">{fmt(totals.ar_balance)}</td>
                  )}
                  <td className="px-4 py-3 text-right tabular-nums text-gray-800">{fmt(totals.gen_retainer)}</td>
                  {canSeeTrust && (
                    <td className="px-4 py-3 text-right tabular-nums text-green-600">{fmt(totals.trust_balance)}</td>
                  )}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
